import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Chip, Divider, CircularProgress,
  Checkbox, FormControlLabel, Tooltip, Alert
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FolderIcon from '@mui/icons-material/Folder';

const CATEGORIES = ['Certifieringar', 'Försäkringar', 'Miljö', 'Ramavtal', 'Övrigt'];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

export default function DocumentPicker({ open, onClose, attachedDocs = [], onAttach }) {
  const [libraryDocs, setLibraryDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    async function load() {
      const snap = await getDocs(collection(db, 'documentLibrary'));
      const items = await Promise.all(snap.docs.map(async d => {
        const versionsSnap = await getDocs(
          query(collection(db, 'documentLibrary', d.id, 'versions'), orderBy('version', 'desc'))
        );
        const versions = versionsSnap.docs.map(v => ({ id: v.id, ...v.data() }));
        return { id: d.id, ...d.data(), versions };
      }));
      items.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
      setLibraryDocs(items);

      // Förifyll redan bifogade
      const preselected = {};
      attachedDocs.forEach(a => { preselected[a.documentId] = true; });
      setSelected(preselected);
      setLoading(false);
    }
    load();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(docId) {
    setSelected(prev => ({ ...prev, [docId]: !prev[docId] }));
  }

  function handleConfirm() {
    const result = libraryDocs
      .filter(d => selected[d.id])
      .map(d => ({
        documentId: d.id,
        name: d.name,
        category: d.category,
        fileUrl: d.currentFileUrl,
        fileName: d.currentFileName,
        version: d.currentVersion,
        expiresAt: d.expiresAt || null
      }));
    onAttach(result);
    onClose();
  }

  const grouped = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = libraryDocs.filter(d => d.category === cat);
    return acc;
  }, {});

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const expiredDocs = libraryDocs.filter(d => {
    const days = daysUntil(d.expiresAt);
    return days !== null && days < 0 && selected[d.id];
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { maxHeight: '80vh' } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderIcon sx={{ color: '#FF6B00' }} />
          <Typography variant="h6" fontWeight="bold">Bifoga från dokumentbiblioteket</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : libraryDocs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              Dokumentbiblioteket är tomt. Lägg till dokument från Dokumentbiblioteket.
            </Typography>
          </Box>
        ) : (
          <>
            {expiredDocs.length > 0 && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                {expiredDocs.map(d => d.name).join(', ')} har gått ut — kontrollera om ny version finns.
              </Alert>
            )}
            {CATEGORIES.map(cat => {
              const catDocs = grouped[cat];
              if (!catDocs.length) return null;
              return (
                <Box key={cat} sx={{ mb: 2 }}>
                  <Typography variant="overline" color="text.secondary"
                    fontWeight="bold" sx={{ letterSpacing: 1.5 }}>
                    {cat}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  {catDocs.map(d => {
                    const days = daysUntil(d.expiresAt);
                    const expired = days !== null && days < 0;
                    const expiringSoon = days !== null && days >= 0 && days <= 30;
                    return (
                      <Box key={d.id} sx={{
                        display: 'flex', alignItems: 'center',
                        py: 0.5, px: 1, borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}>
                        <FormControlLabel
                          sx={{ flex: 1, m: 0 }}
                          control={
                            <Checkbox
                              checked={!!selected[d.id]}
                              onChange={() => toggle(d.id)}
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <InsertDriveFileIcon sx={{
                                fontSize: 16,
                                color: expired ? '#d32f2f' : '#FF6B00'
                              }} />
                              <Typography variant="body2"
                                sx={{ color: expired ? 'error.main' : 'text.primary' }}>
                                {d.name}
                              </Typography>
                              <Chip label={`v${d.currentVersion}`} size="small"
                                sx={{ fontSize: 10, height: 18,
                                  bgcolor: '#1a1a1a', color: 'white' }} />
                              {expired && (
                                <Tooltip title={`Utgick ${d.expiresAt}`}>
                                  <Chip label="Utgången" size="small" color="error"
                                    icon={<WarningAmberIcon style={{ fontSize: 12 }} />}
                                    sx={{ fontSize: 10, height: 18 }} />
                                </Tooltip>
                              )}
                              {expiringSoon && (
                                <Tooltip title={`Går ut om ${days} dagar`}>
                                  <Chip label={`${days}d kvar`} size="small" color="warning"
                                    sx={{ fontSize: 10, height: 18 }} />
                                </Tooltip>
                              )}
                            </Box>
                          }
                        />
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {selectedCount > 0 ? `${selectedCount} dokument valda` : 'Välj dokument att bifoga'}
        </Typography>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" onClick={handleConfirm}>
          Bifoga valda
        </Button>
      </DialogActions>
    </Dialog>
  );
}
